import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import InputField from "../../Components/InputComponent/InputField";
import SelectInputField from "../../Components/InputComponent/SelectInputField";
import "./ModifyTenantModal.scss";

const ModifyTenantFlowModal = ({ isOpen, onClose, axiosInstance }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [userPlans, setUserPlans] = useState(null);
  const [selectedUserPlan, setSelectedUserPlan] = useState(null);
  const [modules, setModules] = useState(null);
  const [selectedModuleIds, setSelectedModuleIds] = useState([]);
  const [pricingPlans, setPricingPlans] = useState(null);
  const [selectedMainPlan, setSelectedMainPlan] = useState(null); // Changed: select entire plan

  const [companyDetails, setCompanyDetails] = useState({
    companyStrength: "",
    adminUsers: "",
  });

  const [quotation, setQuotation] = useState(null);

  const [finalDetails, setFinalDetails] = useState({
    subDomain: "",
    monthPlan: "THREE_MONTHS",
    purchasedAt: new Date().toISOString().split("T")[0],
    validTill: "",
    loginCredentialDTO: {
      email: "",
      password: "",
      name: "",
    },
    amountPaid: 0,
  });

  const [updateAdmin, setUpdateAdmin] = useState(false);

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/tenants/details");

      if (response.data.status === 0 || response.data.status === 200) {
        const normalizedTenants = response.data.data.map((tenant, index) => ({
          tenantRegId: tenant.tenantRegId || index,
          companyName: tenant.companyName,
          email: tenant.email,
          subDomain: tenant.subDomain,
          phoneNumber: tenant.phoneNumber,
          modulesSelected: tenant.modulesSelected || [],
          strength: tenant.strength,
          expiryDate: tenant.expiryDate,
        }));

        setTenants(normalizedTenants);
      }
    } catch (error) {
      console.error("Error fetching tenants:", error);
      toast.error("Failed to load tenants.");
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const [plansResponse, modulesResponse] = await Promise.all([
        axiosInstance.get("/user-plans"),
        axiosInstance.get("/modules"),
      ]);

      if (
        plansResponse.data.status === 0 ||
        plansResponse.data.status === 200
      ) {
        setUserPlans(plansResponse.data.data);
      }

      if (
        modulesResponse.data.status === 0 ||
        modulesResponse.data.status === 200
      ) {
        setModules(modulesResponse.data.data);
      }
    } catch (error) {
      console.error("Error fetching initial data:", error);
      toast.error("Failed to load plans and modules.");
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  const fetchPricingPlans = async () => {
    if (!selectedUserPlan || selectedModuleIds.length === 0) {
      toast.info("Please select a user plan and at least one module");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        userPlanId: selectedUserPlan.userPlanId,
        moduleIds: selectedModuleIds,
      };

      const response = await axiosInstance.post(
        "/purchase/pricing-plan",
        payload
      );

      if (response.data.status === 0 || response.data.status === 200) {
        setPricingPlans(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching pricing plans:", error);
      toast.error("Failed to load pricing plans.");
    } finally {
      setLoading(false);
    }
  };

  const fetchQuotation = async () => {
    setLoading(true);
    try {
      // Get all module IDs from selected main plan
      const modulePricePlanIds = selectedMainPlan.moduleDtos.map(
        (m) => m.modulePricingPlanId
      );

      const payload = {
        modulePricePlanId: modulePricePlanIds,
        companyStrength: Number(companyDetails.companyStrength),
        adminLoginRequired: Number(companyDetails.adminUsers || 0),
      };

      const response = await axiosInstance.post("/purchase/quotation", payload);

      if (response.data.status === 0 || response.data.status === 200) {
        setQuotation(response.data.data);

        setFinalDetails((prev) => ({
          ...prev,
          amountPaid: response.data.data.totalPrice,
        }));

        calculateValidTill(finalDetails.monthPlan);
      }
    } catch (error) {
      console.error("Quotation API failed:", error);
      toast.error("Failed to generate quotation");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && currentStep === 1 && tenants.length === 0) {
      fetchTenants();
    }
  }, [isOpen, currentStep, tenants.length, fetchTenants]);

  useEffect(() => {
    if (selectedTenant) {
      setFinalDetails((prev) => ({
        ...prev,
        subDomain: selectedTenant.subDomain || "",
      }));

      if (selectedTenant.strength) {
        setCompanyDetails((prev) => ({
          ...prev,
          companyStrength: selectedTenant.strength.toString(),
        }));
      }

      if (
        selectedTenant.modulesSelected &&
        selectedTenant.modulesSelected.length > 0 &&
        modules
      ) {
        const moduleIds = modules
          .filter((module) =>
            selectedTenant.modulesSelected.includes(module.moduleName)
          )
          .map((module) => module.moduleId);

        if (moduleIds.length > 0) {
          setSelectedModuleIds(moduleIds);
        }
      }

      if (userPlans && selectedTenant.strength) {
        const matchingPlan = userPlans.find(
          (plan) =>
            selectedTenant.strength >= plan.minimumUser &&
            (plan.maximumUser === 0 ||
              selectedTenant.strength <= plan.maximumUser)
        );

        if (matchingPlan) {
          setSelectedUserPlan(matchingPlan);
        }
      }
    }
  }, [selectedTenant, modules, userPlans]);

  useEffect(() => {
    if (isOpen && currentStep === 2 && (!userPlans || !modules)) {
      fetchInitialData();
    }
  }, [isOpen, currentStep, userPlans, modules, fetchInitialData]);

  const calculateValidTill = (monthPlan) => {
    const purchaseDate = new Date(finalDetails.purchasedAt);
    let months =
      monthPlan === "SIX_MONTHS" ? 6 : monthPlan === "TWELVE_MONTHS" ? 12 : 3;
    purchaseDate.setMonth(purchaseDate.getMonth() + months);
    setFinalDetails((prev) => ({
      ...prev,
      validTill: purchaseDate.toISOString().split("T")[0],
    }));
  };

  const handleModuleSelection = (moduleId) => {
    setSelectedModuleIds((prev) =>
      prev.includes(moduleId)
        ? prev.filter((id) => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  // New: Handle Select All
  const handleSelectAllModules = () => {
    if (selectedModuleIds.length === modules.length) {
      setSelectedModuleIds([]);
    } else {
      setSelectedModuleIds(modules.map((m) => m.moduleId));
    }
  };

  // New: Handle main plan selection
  const handleMainPlanSelection = (mainPlan) => {
    setSelectedMainPlan(mainPlan);
  };

  const handleNextStep = () => {
    if (currentStep === 1) {
      if (!selectedTenant) {
        toast.info("Please select a tenant");
        return;
      }
      setFinalDetails((prev) => ({
        ...prev,
        subDomain: selectedTenant.subDomain || "",
      }));
    }

    if (currentStep === 2) {
      if (!selectedUserPlan || selectedModuleIds.length === 0) {
        toast.info("Please select a user plan and at least one module");
        return;
      }
      fetchPricingPlans();
    }

    if (currentStep === 3 && !selectedMainPlan) {
      toast.info("Please select a pricing plan");
      return;
    }

    if (currentStep === 4) {
      if (
        !companyDetails.companyStrength ||
        companyDetails.companyStrength < 1
      ) {
        toast.info("Please enter company strength");
        return;
      }
      if (selectedUserPlan) {
        if (companyDetails.companyStrength < selectedUserPlan.minimumUser) {
          toast.info(
            `Company strength must be at least ${selectedUserPlan.minimumUser} users`
          );
          return;
        }
        if (
          selectedUserPlan.maximumUser > 0 &&
          companyDetails.companyStrength > selectedUserPlan.maximumUser
        ) {
          toast.info(
            `Company strength cannot exceed ${selectedUserPlan.maximumUser} users`
          );
          return;
        }
      }
      fetchQuotation();
    }

    setCurrentStep((prev) => prev + 1);
  };

  const handleSubmit = async () => {
    if (!finalDetails.subDomain) {
      toast.info("Please enter subdomain");
      return;
    }

    if (updateAdmin) {
      if (
        !finalDetails.loginCredentialDTO.email ||
        !finalDetails.loginCredentialDTO.password ||
        !finalDetails.loginCredentialDTO.name
      ) {
        toast.info("Please fill all admin details or uncheck 'Update Admin'");
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        tenantRegId: selectedTenant.tenantRegId,
        subDomain: finalDetails.subDomain,
        monthPlan: finalDetails.monthPlan,
        purchasedAt: finalDetails.purchasedAt,
        validTill: finalDetails.validTill,
        modulepricingPlanIds: selectedMainPlan.moduleDtos.map(
          (m) => m.modulePricingPlanId
        ),
        loginCredentialDTO: updateAdmin
          ? {
              email: finalDetails.loginCredentialDTO.email || "",
              password: finalDetails.loginCredentialDTO.password || "",
              name: finalDetails.loginCredentialDTO.name || "",
            }
          : { email: "", password: "", name: "" },
        baseUserPlan: quotation.baseUserPlan,
        companyStrength: Number(companyDetails.companyStrength),
        adminLoginRequired: Number(companyDetails.adminUsers || 0),
        adminUserPricePerUser: quotation.adminUserPricePerUser,
        pricePerUser:
          quotation.totalPrice / companyDetails.companyStrength || 0,
        serverCost: quotation.serverCost,
        totalAmmount: quotation.totalPrice,
        amountPaid: finalDetails.amountPaid,
      };

      await axiosInstance.post("/purchase/modules", payload);
      toast.success("Tenant modified successfully!");
      onClose();
      window.location.reload();
    } catch (error) {
      const status = error?.response?.status;
      const rawMessage =
        error?.response?.data?.message || error?.response?.data?.error;
      const message = Array.isArray(rawMessage)
        ? rawMessage.join(", ")
        : rawMessage || "Something went wrong.";

      if (status === 409 || status === 500) {
        toast.error(message);
      } else {
        toast.error("Failed to modify tenant.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modify-modal-overlay">
      <div className="modify-modal">
        <button className="close-btn" onClick={onClose}>
          ×
        </button>

        <div className="progress-bar">
          {[1, 2, 3, 4, 5, 6].map((step) => (
            <div
              key={step}
              className={`progress-step ${currentStep >= step ? "active" : ""}`}
            >
              {step}
            </div>
          ))}
        </div>

        {loading && (
          <div className="loading-overlay">
            <div className="spinner"></div>
          </div>
        )}

        {/* Step 1: Tenant Selection */}
        {currentStep === 1 && (
          <div className="step-content">
            <h2 className="step-title">Select Tenant to Modify</h2>

            <div className="tenants-grid">
              {tenants.map((tenant) => (
                <div
                  key={tenant.tenantRegId}
                  className={`tenant-card ${
                    selectedTenant?.tenantRegId === tenant.tenantRegId
                      ? "selected"
                      : ""
                  }`}
                  onClick={() => setSelectedTenant(tenant)}
                >
                  <input
                    type="radio"
                    checked={selectedTenant?.tenantRegId === tenant.tenantRegId}
                    onChange={() => setSelectedTenant(tenant)}
                  />
                  <div className="tenant-info">
                    <div className="tenant-name">{tenant.companyName}</div>
                    <div className="tenant-details">
                      {tenant.email} • {tenant.subDomain}
                    </div>
                    <div className="tenant-meta">
                      Strength: {tenant.strength} • Expires: {tenant.expiryDate}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={handleNextStep}
              className="next-btn"
              disabled={!selectedTenant}
            >
              Next: Select Plan & Modules
            </button>
          </div>
        )}

        {/* Step 2: Plan & Module Selection */}
        {currentStep === 2 && (
          <div className="step-content">
            <h2 className="step-title">Select Plan & Modules</h2>

            {selectedTenant && (
              <div className="info-banner">
                <strong>Modifying:</strong> {selectedTenant.companyName}
                {selectedUserPlan && (
                  <span> • Auto-selected matching plan</span>
                )}
                {selectedModuleIds.length > 0 && (
                  <span>
                    {" "}
                    • {selectedModuleIds.length} modules pre-selected
                  </span>
                )}
              </div>
            )}

            <div className="section">
              <h3 className="section-subtitle">Choose User Plan</h3>
              <div className="plans-grid">
                {userPlans?.map((plan) => (
                  <div
                    key={plan.userPlanId}
                    className={`plan-card ${
                      selectedUserPlan?.userPlanId === plan.userPlanId
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => setSelectedUserPlan(plan)}
                  >
                    <input
                      type="radio"
                      checked={selectedUserPlan?.userPlanId === plan.userPlanId}
                      onChange={() => setSelectedUserPlan(plan)}
                    />
                    <div className="plan-info">
                      <div className="plan-details">
                        {plan.minimumUser} –{" "}
                        {plan.maximumUser > 0 ? plan.maximumUser : "Unlimited"}{" "}
                        users
                      </div>
                      {plan.basePlan && (
                        <span className="badge">Base Plan</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {modules && (
              <div className="section">
                <div className="section-header">
                  <h3 className="section-subtitle">Select Modules</h3>
                  <button
                    onClick={handleSelectAllModules}
                    className="select-all-btn"
                  >
                    {selectedModuleIds.length === modules.length
                      ? "Deselect All"
                      : "Select All"}
                  </button>
                </div>
                <div className="modules-grid">
                  {modules.map((module) => (
                    <div
                      key={module.moduleId}
                      className={`module-card ${
                        selectedModuleIds.includes(module.moduleId)
                          ? "selected"
                          : ""
                      }`}
                      onClick={() => handleModuleSelection(module.moduleId)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedModuleIds.includes(module.moduleId)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleModuleSelection(module.moduleId)}
                      />

                      <div className="module-content">
                        <div className="module-name">{module.moduleName}</div>
                        {module.moduleDescription && (
                          <div className="module-description">
                            {module.moduleDescription}
                          </div>
                        )}
                        {module.moduleFeatures?.length > 0 && (
                          <ul className="feature-list">
                            {module.moduleFeatures
                              .slice(0, 2)
                              .map((feature) => (
                                <li key={feature.moduleFeatureId}>
                                  {feature.featureName}
                                </li>
                              ))}
                            {module.moduleFeatures.length > 2 && (
                              <li>+{module.moduleFeatures.length - 2} more</li>
                            )}
                          </ul>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="button-group">
              <button onClick={() => setCurrentStep(1)} className="prev-btn">
                Previous
              </button>
              <button
                onClick={handleNextStep}
                className="next-btn"
                disabled={!selectedUserPlan || selectedModuleIds.length === 0}
              >
                Next: View Pricing
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Pricing Plans - Select Main Plan */}
        {currentStep === 3 && pricingPlans && (
          <div className="step-content">
            <h2 className="step-title">Select Pricing Plan</h2>

            {selectedUserPlan && (
              <div className="info-banner">
                <strong>Selected Plan:</strong>{" "}
                {selectedUserPlan.basePlan ? "Base Plan" : "Custom Plan"} (
                {selectedUserPlan.minimumUser} –{" "}
                {selectedUserPlan.maximumUser > 0
                  ? selectedUserPlan.maximumUser
                  : "Unlimited"}{" "}
                users)
              </div>
            )}

            <div className="pricing-plans-selection">
              {pricingPlans.map((mainPlan) => (
                <div
                  key={mainPlan.mainPlanId}
                  className={`main-plan-card ${
                    selectedMainPlan?.mainPlanId === mainPlan.mainPlanId
                      ? "selected"
                      : ""
                  }`}
                  onClick={() => handleMainPlanSelection(mainPlan)}
                >
                  <input
                    type="radio"
                    checked={
                      selectedMainPlan?.mainPlanId === mainPlan.mainPlanId
                    }
                    onChange={() => handleMainPlanSelection(mainPlan)}
                  />
                  <div className="plan-content">
                    <h3 className="main-plan-title">{mainPlan.mainPlanName}</h3>
                    {mainPlan.adminLoginPricePerUser > 0 && (
                      <div className="admin-price-tag">
                        Admin: ₹{mainPlan.adminLoginPricePerUser}/user
                      </div>
                    )}

                    <div className="modules-summary">
                      <strong>
                        Includes {mainPlan.moduleDtos.length} modules:
                      </strong>
                      <ul className="module-list">
                        {mainPlan.moduleDtos.map((module) => (
                          <li key={module.moduleId}>
                            {module.moduleName} - ₹{module.modulePricePerUser}
                            /user
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="button-group">
              <button onClick={() => setCurrentStep(2)} className="prev-btn">
                Previous
              </button>
              <button
                onClick={handleNextStep}
                className="next-btn"
                disabled={!selectedMainPlan}
              >
                Next: Company Details
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Company Details */}
        {currentStep === 4 && (
          <div className="step-content">
            <h2 className="step-title">Company Details</h2>

            {companyDetails.companyStrength && (
              <div className="info-banner">
                Company strength auto-filled from existing tenant data
              </div>
            )}

            <div className="form-grid">
              <InputField
                label="Company Strength"
                required
                placeholder="Enter number of employees"
                type="number"
                value={companyDetails.companyStrength}
                onChange={(e) =>
                  setCompanyDetails((prev) => ({
                    ...prev,
                    companyStrength: e.target.value,
                  }))
                }
              />

              <InputField
                label="Number of Admin Users"
                type="number"
                placeholder="Enter number of admin users"
                value={companyDetails.adminUsers}
                onChange={(e) =>
                  setCompanyDetails((prev) => ({
                    ...prev,
                    adminUsers: e.target.value,
                  }))
                }
                min="0"
              />
            </div>

            {selectedMainPlan && (
              <div className="selected-modules">
                <h4>Selected Plan: {selectedMainPlan.mainPlanName}</h4>
                <div className="modules-list">
                  {selectedMainPlan.moduleDtos.map((module) => (
                    <div key={module.moduleId} className="module-item">
                      • {module.moduleName} - ₹{module.modulePricePerUser}/user
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="button-group">
              <button onClick={() => setCurrentStep(3)} className="prev-btn">
                Previous
              </button>
              <button onClick={handleNextStep} className="next-btn">
                Next: View Quotation
              </button>
            </div>
          </div>
        )}

        {/* Step 5: Quotation */}
        {currentStep === 5 && quotation && (
          <div className="step-content">
            <h2 className="step-title">Order Summary</h2>

            <div className="summary-layout">
              <div className="summary-left">
                <div className="order-info-card">
                  <div className="info-row">
                    <span>Tenant:</span>
                    <span>{selectedTenant.companyName}</span>
                  </div>
                  <div className="info-row">
                    <span>Subdomain:</span>
                    <span>{finalDetails.subDomain}.ourdept.com</span>
                  </div>
                  <div className="info-row">
                    <span>Plan Type:</span>
                    <span>{quotation.mainPlanName}</span>
                  </div>
                  <div className="info-row">
                    <span>Subscription:</span>
                    <span>{quotation.subscriptionType}</span>
                  </div>
                  <div className="info-row">
                    <span>Company Size:</span>
                    <span>{quotation.companyStrength} users</span>
                  </div>
                  <div className="info-row">
                    <span>Admin Users:</span>
                    <span>{quotation.adminUsers}</span>
                  </div>
                </div>

                <div className="modules-section">
                  <h4>Selected Modules</h4>
                  {quotation.moduleDtos.map((module, idx) => (
                    <div key={module.moduleId} className="module-item">
                      {idx + 1}. {module.moduleName}
                    </div>
                  ))}
                </div>
              </div>

              <div className="summary-right">
                <div className="price-card">
                  <h3>Order Summary</h3>

                  {quotation.adminUserPricePerUser > 0 && (
                    <div className="price-row">
                      <span>Admin Users:</span>
                      <span>
                        ₹
                        {quotation.adminUserPricePerUser * quotation.adminUsers}
                      </span>
                    </div>
                  )}

                  {quotation.serverCost > 0 && (
                    <div className="price-row">
                      <span>Server Cost:</span>
                      <span>₹{quotation.serverCost}</span>
                    </div>
                  )}

                  <div className="divider"></div>

                  <div className="total-row">
                    <span>Total:</span>
                    <span className="total-price">
                      ₹{quotation.totalPrice.toLocaleString("en-IN")}
                    </span>
                  </div>

                  <button onClick={handleNextStep} className="checkout-btn">
                    Proceed to Checkout
                  </button>
                </div>
              </div>
            </div>

            <button onClick={() => setCurrentStep(4)} className="prev-btn">
              Previous
            </button>
          </div>
        )}

        {/* Step 6: Final Details */}
        {currentStep === 6 && quotation && (
          <div className="step-content">
            <h2 className="step-title">Complete Modification</h2>

            <div className="form-section">
              <h3>Subscription Details</h3>
              <div className="form-grid">
                <div className="subdomain-field">
                  <InputField
                    label="Subdomain"
                    required
                    value={finalDetails.subDomain}
                    onChange={(e) =>
                      setFinalDetails((prev) => ({
                        ...prev,
                        subDomain: e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9-]/g, ""),
                      }))
                    }
                  />
                  <span className="domain-suffix">.ourdept.com</span>
                </div>

                <SelectInputField
                  label="Duration"
                  value={finalDetails.monthPlan}
                  onChange={(e) => {
                    setFinalDetails((prev) => ({
                      ...prev,
                      monthPlan: e.target.value,
                    }));
                    calculateValidTill(e.target.value);
                  }}
                  options={[
                    { value: "THREE_MONTHS", label: "3 Months" },
                    { value: "SIX_MONTHS", label: "6 Months" },
                    { value: "TWELVE_MONTHS", label: "12 Months" },
                  ]}
                />

                <InputField
                  label="Purchase Date"
                  type="date"
                  value={finalDetails.purchasedAt}
                  onChange={(e) => {
                    setFinalDetails((prev) => ({
                      ...prev,
                      purchasedAt: e.target.value,
                    }));
                    calculateValidTill(finalDetails.monthPlan);
                  }}
                />

                <InputField
                  label="Valid Till"
                  type="date"
                  value={finalDetails.validTill}
                  onChange={(e) =>
                    setFinalDetails((prev) => ({
                      ...prev,
                      validTill: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className="form-section admin-section">
              <div className="admin-header">
                <h3>Admin Credentials (Optional)</h3>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={updateAdmin}
                    onChange={(e) => setUpdateAdmin(e.target.checked)}
                  />
                  <span>Update/Create New Admin</span>
                </label>
              </div>

              <div className="admin-note">
                <strong>Note:</strong> Leave empty to keep existing admin. Fill
                all fields to create new admin.
              </div>

              {updateAdmin && (
                <div className="form-grid">
                  <InputField
                    label="Admin Name"
                    required
                    value={finalDetails.loginCredentialDTO.name}
                    onChange={(e) =>
                      setFinalDetails((prev) => ({
                        ...prev,
                        loginCredentialDTO: {
                          ...prev.loginCredentialDTO,
                          name: e.target.value,
                        },
                      }))
                    }
                    placeholder="John Doe"
                  />

                  <InputField
                    label="Admin Email"
                    required
                    type="email"
                    value={finalDetails.loginCredentialDTO.email}
                    onChange={(e) =>
                      setFinalDetails((prev) => ({
                        ...prev,
                        loginCredentialDTO: {
                          ...prev.loginCredentialDTO,
                          email: e.target.value,
                        },
                      }))
                    }
                    placeholder="admin@company.com"
                  />

                  <div className="password-field">
                    <InputField
                      label="Password"
                      required
                      type={showPassword ? "text" : "password"}
                      value={finalDetails.loginCredentialDTO.password}
                      onChange={(e) =>
                        setFinalDetails((prev) => ({
                          ...prev,
                          loginCredentialDTO: {
                            ...prev.loginCredentialDTO,
                            password: e.target.value,
                          },
                        }))
                      }
                      placeholder="Minimum 6 characters"
                      minLength="6"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="password-toggle"
                    >
                      {showPassword ? "👁️" : "🙈"}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="form-section">
              <h3>Payment</h3>
              <div className="payment-summary">
                <span>Total Amount:</span>
                <span className="total">
                  ₹{quotation.totalPrice.toLocaleString("en-IN")}
                </span>
              </div>

              <InputField
                label="Amount Paid"
                type="number"
                value={finalDetails.amountPaid}
                onChange={(e) =>
                  setFinalDetails((prev) => ({
                    ...prev,
                    amountPaid: parseFloat(e.target.value) || 0,
                  }))
                }
                min="0"
                max={quotation.totalPrice}
                placeholder="0"
              />
            </div>

            <div className="button-group">
              <button onClick={() => setCurrentStep(5)} className="prev-btn">
                Previous
              </button>
              <button
                onClick={handleSubmit}
                className="submit-btn"
                disabled={loading}
              >
                {loading ? "Updating..." : "Complete Modification"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModifyTenantFlowModal;
